import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const BRAND       = "PRZESTRZEŃ".split("");
const DURATION_MS = 2600;

// eased = 1 - (1-t)^3 = 0.92  →  t ≈ 0.569
const MA_SHOW = Math.round(0.569 * DURATION_MS); // 1479 ms — ≈ 92% eased progress
const MA_HIDE = MA_SHOW + 700;                   // visible for 700 ms

function Grain() {
  return (
    <svg
      aria-hidden="true"
      style={{
        position: "absolute", inset: 0,
        width: "100%", height: "100%",
        opacity: 0.04, mixBlendMode: "overlay",
        pointerEvents: "none", zIndex: 1,
      }}
    >
      <filter id="loader-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#loader-grain)" />
    </svg>
  );
}

function Corner({ pos, delay, children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.6 }}
      style={{
        position: "absolute",
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: "0.58rem",
        letterSpacing: "0.26em",
        color: "rgba(242,232,216,0.14)",
        textTransform: "uppercase",
        zIndex: 2,
        ...pos,
      }}
    >
      {children}
    </motion.div>
  );
}

function CharRise({ chars, baseDelay, step = 0.042, size, weight = 300, family, color, spacing }) {
  return (
    <div style={{ overflow: "hidden", display: "flex", justifyContent: "center" }}>
      {chars.map((ch, i) => (
        <motion.span
          key={i}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.72, delay: baseDelay + i * step, ease: [0.22, 1, 0.36, 1] }}
          style={{
            display: "inline-block",
            fontFamily: family, fontSize: size, fontWeight: weight,
            color, letterSpacing: spacing, lineHeight: 1, whiteSpace: "pre",
          }}
        >
          {ch}
        </motion.span>
      ))}
    </div>
  );
}

export default function PageLoader() {
  const [count, setCount] = useState(0);
  const [done,  setDone]  = useState(false);
  const [showMa, setShowMa] = useState(false);

  useEffect(() => {
    // Progress counter
    const start = Date.now();
    const tick = () => {
      const t      = Math.min((Date.now() - start) / DURATION_MS, 1);
      const eased  = 1 - Math.pow(1 - t, 3);
      setCount(Math.floor(eased * 100));
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        setCount(100);
        setTimeout(() => setDone(true), 380);
      }
    };
    requestAnimationFrame(tick);

    // 間 — appears at ~92% progress, gone well before loader exits
    const t1 = setTimeout(() => setShowMa(true),  MA_SHOW);
    const t2 = setTimeout(() => setShowMa(false), MA_HIDE);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const brandEnd = 0.09 + (BRAND.length - 1) * 0.042 + 0.72;

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="loader"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.95, ease: [0.76, 0, 0.24, 1] }}
          style={{
            position: "fixed", inset: 0, zIndex: 9500,
            background: "#100F0C",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Grain />

          {/* Amber atmospheric glow — dims imperceptibly while 間 is present */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -55%)",
              width: 700, height: 700, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(200,121,65,0.075) 0%, transparent 62%)",
              filter: "blur(60px)",
              pointerEvents: "none", zIndex: 0,
              opacity: showMa ? 0.72 : 1,
              transition: "opacity 0.65s ease",
            }}
          />

          {/* Ice accent — bottom-left depth */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: "5%", left: "10%",
              width: 440, height: 440, borderRadius: "50%",
              background: "radial-gradient(circle, rgba(124,196,216,0.04) 0%, transparent 65%)",
              filter: "blur(64px)",
              pointerEvents: "none", zIndex: 0,
              opacity: showMa ? 0.68 : 1,
              transition: "opacity 0.65s ease",
            }}
          />

          {/* Scanning line */}
          <motion.div
            aria-hidden="true"
            initial={{ top: "-1px" }}
            animate={{ top: "100vh" }}
            transition={{ duration: 1.6, delay: 0.2, ease: "linear" }}
            style={{
              position: "absolute", left: 0, right: 0, height: 1,
              background: "linear-gradient(to right, transparent 0%, rgba(200,121,65,0.5) 35%, rgba(200,121,65,0.5) 65%, transparent 100%)",
              pointerEvents: "none", zIndex: 1,
            }}
          />

          {/* ── 間 — moment of pause ── */}
          <AnimatePresence>
            {showMa && (
              <motion.span
                key="ma"
                initial={{ opacity: 0,   filter: "blur(6px)" }}
                animate={{ opacity: 0.34, filter: "blur(0px)" }}
                exit={{    opacity: 0,   filter: "blur(6px)" }}
                transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: "71%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
                  fontWeight: 300,
                  fontSize: "clamp(1.4rem, 2.4vw, 2rem)",
                  lineHeight: 1,
                  letterSpacing: "0.06em",
                  background: "linear-gradient(135deg, #C87941 0%, #7CC4D8 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  pointerEvents: "none",
                  userSelect: "none",
                  zIndex: 3,
                }}
              >
                間
              </motion.span>
            )}
          </AnimatePresence>

          {/* ── Brand block ── */}
          <div style={{ position: "relative", textAlign: "center", zIndex: 2 }}>
            <CharRise
              chars={BRAND}
              baseDelay={0.09}
              size="clamp(2.6rem, 7.5vw, 6rem)"
              family='"Cormorant Garamond", Georgia, serif'
              color="#F2E8D8"
              spacing="0.1em"
            />

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.55, delay: brandEnd - 0.1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                height: 1, width: 48, margin: "0.75rem auto",
                background: "linear-gradient(to right, #C87941, #7CC4D8)",
                transformOrigin: "left",
              }}
            />

            <div style={{ overflow: "hidden" }}>
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.5, delay: brandEnd - 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  display: "inline-block",
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: "clamp(0.58rem, 1vw, 0.76rem)",
                  letterSpacing: "0.58em",
                  color: "#C87941",
                  textTransform: "uppercase",
                }}
              >
                APPS
              </motion.span>
            </div>
          </div>

          <Corner pos={{ top: "2rem", left: "2.5rem" }}   delay={1.1}>© 2026</Corner>
          <Corner pos={{ top: "2rem", right: "2.5rem" }}  delay={1.1}>Poznań · PL</Corner>
          <Corner pos={{ bottom: "2.5rem", left: "2.5rem" }} delay={1.2}>Web · Apps · SaaS</Corner>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            style={{
              position: "absolute", bottom: "2.5rem", right: "2.5rem",
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: "0.65rem", letterSpacing: "0.12em",
              color: "rgba(242,232,216,0.25)", zIndex: 2,
            }}
          >
            {String(count).padStart(3, "0")}
          </motion.div>

          <div
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 1,
              background: "rgba(255,255,255,0.04)", zIndex: 2,
            }}
          >
            <div
              style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(to right, #C87941, #7CC4D8)",
                transformOrigin: "left",
                transform: `scaleX(${count / 100})`,
                transition: "transform 0.1s linear",
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
